import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArrowClockwise,
  BookmarkSimple,
  CheckCircle,
  Clock,
  DotsThreeVertical,
  Eye,
  Handshake,
  ListBullets,
  MagnifyingGlass,
  Plus,
  Rows,
  SquaresFour,
  Tray,
  UserCircle,
  UserCircleDashed,
  UsersThree,
  X,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import {
  DONE_STATUSES,
  EXPORT_TABS,
  HISTORY_TABS,
  MIN_TEAM_SIZE_FOR_HISTORY,
  PRIORITY,
  PRIORITY_LABEL,
  STATUS_LABEL,
  TICKET_STATUS,
  type Priority,
  type TicketSort,
  type TicketStatus,
  type TicketTab,
} from '@helpdesk/shared';
import { deleteJson, getJson, postJson } from '../api/client.ts';
import { meQuery, savedViewsQuery, selectableSites, selectableTeams, sitesQuery, teamsQuery, workersQuery } from '../api/queries.ts';
import type { SavedView, TicketCounts, TicketListItem, TicketListResult } from '../api/types.ts';
import { AssignDialog, Avatar, WaitingFor, takeTicket, type AssignTarget } from '../components/assign.tsx';
import { OriginBadge, PriorityBadge, StatusBadge } from '../components/badges.tsx';
import { Button, EmptyState, errorMessage, ErrorNote, Input, Modal, Notice, PageTitle, Spinner, Tabs } from '../components/ui.tsx';
import { fmtAge, fmtDate, fmtDateTime } from '../lib/format.ts';
import { useAction } from '../lib/use-action.ts';

/**
 * Ticketübersicht nach dem Vorbild InvGate Service Desk:
 *   - links Ansichten mit Zählern (Offen, Nicht zugewiesen, Mir zugewiesen …),
 *     darunter die Teams („Help Desks“) und eigene gespeicherte Ansichten
 *   - oben eine Filterleiste, alles steht in der Adresse (teilbar, Zurück-Taste)
 *   - Tabelle mit „Wartet auf“, Zuständig (Team + Person), Melder, letzter Änderung
 *   - je Zeile „Übernehmen“ und „Zuweisen“, für mehrere Zeilen als Sammelaktion
 * Melder sehen weiterhin ihre schlichte Liste „Meine Meldungen“.
 */
type TicketSearch = {
  tab?: TicketTab;
  siteId?: string;
  priority?: Priority;
  status?: TicketStatus;
  q?: string;
  machineId?: string;
  teamId?: string;
  assignee?: string;
  sort?: TicketSort;
  page?: number;
  ansicht?: 'liste' | 'karten';
};

const PAGE_SIZE = 50;

type CountKey = keyof NonNullable<TicketCounts>;

const VIEWS: Array<{ id: TicketTab; label: string; count?: CountKey; icon: IconName }> = [
  { id: 'open', label: 'Offen', count: 'open', icon: 'inbox' },
  { id: 'unassigned', label: 'Nicht zugewiesen', count: 'unassigned', icon: 'userQuestion' },
  { id: 'mine', label: 'Mir zugewiesen', count: 'mine', icon: 'user' },
  { id: 'involved', label: 'Hinzugezogen', count: 'involved', icon: 'handshake' },
  { id: 'waiting', label: 'Wartend', count: 'waiting', icon: 'clock' },
  { id: 'review', label: 'Prüfen', count: 'review', icon: 'eye' },
  { id: 'resolved', label: 'Behoben', count: 'resolved', icon: 'check' },
  { id: 'done', label: 'Erledigt', icon: 'archive' },
  { id: 'all', label: 'Alle', icon: 'list' },
];

const SORT_LABEL: Record<TicketSort, string> = {
  priority: 'Priorität, dann Alter',
  updated: 'Letzte Änderung',
  created: 'Neueste zuerst',
  number: 'Nummer absteigend',
};

export function TicketsPage() {
  const me = useQuery(meQuery);
  const worker = me.data?.user.role === 'technician' || me.data?.user.role === 'lead';
  return worker ? <WorkerTickets /> : <ReporterTickets />;
}

// ---------------------------------------------------------------------------
// Instandhaltung: InvGate-Ansicht
// ---------------------------------------------------------------------------

function WorkerTickets() {
  const search = useSearch({ from: '/app/tickets' }) as TicketSearch;
  const navigate = useNavigate({ from: '/tickets' });
  const qc = useQueryClient();
  const me = useQuery(meQuery);
  const isLead = me.data?.user.role === 'lead';
  const allSites = useQuery(sitesQuery).data;
  // Die Leitung erreicht ein fremdes Werk nur über diesen Filter, und ein stillgelegtes nimmt weiter Meldungen an.
  // Sonst bleibt es nur, solange eine gespeicherte Ansicht darauf zeigt: Der Filter ist dann sichtbar und lösbar.
  const sites = selectableSites(allSites, isLead ? allSites?.map((s) => s.id) : [search.siteId]);
  const teams = useQuery({ ...teamsQuery, select: (rows) => selectableTeams(rows, allSites, search.teamId) });
  const views = useQuery(savedViewsQuery);
  const workers = useQuery(workersQuery(search.siteId));

  const tab: TicketTab = search.tab ?? 'open';
  const historyTab = HISTORY_TABS.includes(tab);
  const page = search.page ?? 1;
  const cards = search.ansicht === 'karten';
  const [q, setQ] = useState(search.q ?? '');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assign, setAssign] = useState<AssignTarget[] | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const removeView = useAction();

  const go = (patch: Partial<TicketSearch>, keepPage = false) =>
    void navigate({ search: (s: TicketSearch) => ({ ...s, ...patch, ...(keepPage ? {} : { page: undefined }) }) });

  useEffect(() => {
    const t = setTimeout(() => {
      if ((search.q ?? '') !== q) go({ q: q || undefined });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, search.q]);
  useEffect(() => setQ(search.q ?? ''), [search.q]);

  // Abgeschlossene Tickets gibt es nicht je Kollege (§14.1). Eine Person kann per Reiter,
  // gespeicherter Ansicht oder Link hier ankommen und fliegt aus der Adresse; nur die eigene bleibt.
  const meId = me.data?.user.id;
  const personFilter = Boolean(search.assignee) && search.assignee !== 'me' && search.assignee !== 'none';
  useEffect(() => {
    if (!historyTab || !personFilter || !meId) return;
    void navigate({ search: (s: TicketSearch) => ({ ...s, assignee: s.assignee === meId ? 'me' : undefined, page: undefined }), replace: true });
  }, [historyTab, personFilter, meId, navigate]);

  const params = {
    tab,
    siteId: search.siteId,
    priority: search.priority,
    status: search.status,
    q: search.q,
    machineId: search.machineId,
    teamId: search.teamId,
    assignee: search.assignee,
    sort: search.sort,
    page,
    pageSize: PAGE_SIZE,
  };
  const list = useQuery({
    queryKey: ['tickets', 'list', params],
    queryFn: () => getJson<TicketListResult & { counts: TicketCounts }>('/api/tickets', params),
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });
  const paramKey = JSON.stringify({ ...params, page: undefined });
  useEffect(() => setSelected(new Set()), [paramKey]);

  // `keepPreviousData` hält die alte Antwort, bis die neue da ist. Ohne Netz bleibt sie stehen und gehört zu einem anderen Filter.
  const staleList = list.isPaused && list.isPlaceholderData;
  const shown = staleList ? undefined : list.data;
  const counts = shown?.counts ?? null;
  const items = shown?.items ?? [];
  const team = teams.data?.find((t) => t.id === search.teamId);
  const view = VIEWS.find((v) => v.id === tab)!;
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['tickets'] });
    await qc.invalidateQueries({ queryKey: ['teams'] });
  };

  const take = async (rows: TicketListItem[]) => {
    setError(null);
    const failed: string[] = [];
    for (const t of rows) {
      try {
        await takeTicket(t);
      } catch (err) {
        failed.push(`${t.display}: ${errorMessage(err)}`);
      }
    }
    if (failed.length) setError(new Error(failed.join('\n')));
    setSelected(new Set());
    await refresh();
  };

  const currentQuery = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(search)) if (v !== undefined && v !== '' && k !== 'page') out[k] = String(v);
    return out;
  }, [search]);
  const activeView = views.data?.find((v) => JSON.stringify(sortKeys(v.query)) === JSON.stringify(sortKeys(currentQuery)));

  const filtersActive = Boolean(search.status || search.priority || search.siteId || search.assignee || search.q || search.machineId);
  const selectedRows = items.filter((t) => selected.has(t.id));
  const exportUrl = `/api/tickets/export.csv?${new URLSearchParams(
    Object.entries({ tab, siteId: search.siteId, priority: search.priority, status: search.status, q: search.q, teamId: search.teamId }).filter(([, v]) => v) as [string, string][],
  ).toString()}`;

  const sidebar = (
    <nav className="space-y-6" aria-label="Ansichten">
      <div>
        <p className="ad-section-title mb-2 px-3 text-[16px]">Ansichten</p>
        <ul className="space-y-px">
          {VIEWS.map((v) => (
            <li key={v.id}>
              <SideItem
                active={tab === v.id && !search.teamId && !activeView}
                onClick={() => void navigate({ search: { tab: v.id } })}
                icon={<Icon name={v.icon} />}
                label={v.label}
                count={v.count && counts ? counts[v.count] : undefined}
                alert={v.id === 'unassigned' && Boolean(counts?.unassigned)}
              />
            </li>
          ))}
        </ul>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between px-3">
          <p className="ad-section-title text-[16px]">Teams</p>
          {isLead ? (
            <Link to="/teams" className="ad-link text-[14px]">
              Pflegen
            </Link>
          ) : null}
        </div>
        <ul className="space-y-px">
          {(teams.data ?? [])
            .filter((t) => t.isActive)
            .map((t) => (
              <li key={t.id}>
                <SideItem
                  active={search.teamId === t.id}
                  onClick={() => void navigate({ search: { tab: 'open', teamId: t.id } })}
                  icon={<UsersThree size={20} weight={search.teamId === t.id ? 'fill' : 'regular'} />}
                  label={t.name}
                  sub={sites.length > 1 && !t.name.includes(t.siteCode) ? t.siteCode : undefined}
                  count={t.openCount}
                />
              </li>
            ))}
        </ul>
      </div>

      <div>
        <p className="ad-section-title mb-2 px-3 text-[16px]">Eigene Ansichten</p>
        <ul className="space-y-px">
          {(views.data ?? []).map((v) => (
            <li key={v.id} className="group relative">
              <SideItem active={activeView?.id === v.id} onClick={() => void navigate({ search: v.query as TicketSearch })} icon={<Icon name="bookmark" />} label={v.name} />
              <button
                type="button"
                aria-label={`Ansicht „${v.name}“ löschen`}
                className="absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer p-1 text-steel-400 opacity-0 group-hover:opacity-100 hover:text-arnold-red focus:opacity-100"
                disabled={removeView.busy}
                onClick={() =>
                  void removeView.run(async () => {
                    await deleteJson(`/api/views/${v.id}`);
                    await qc.invalidateQueries({ queryKey: ['views'] });
                  })
                }
              >
                <Icon name="x" />
              </button>
            </li>
          ))}
        </ul>
        {/* Hier und nicht über der Liste: Die Spalte reicht weiter nach unten, die Meldung läge beim Klicken außer Sicht. */}
        {removeView.error ? <ErrorNote error={removeView.error} /> : null}
        <button type="button" className="mt-1 flex min-h-10 w-full cursor-pointer items-center gap-2 px-3 text-left text-[15px] text-arnold-red hover:bg-arnold-red-tint" onClick={() => setSaveOpen(true)}>
          <Icon name="plus" /> Ansicht speichern
        </button>
      </div>
    </nav>
  );

  return (
    <div>
      <PageTitle
        action={
          <div className="flex flex-wrap gap-2">
            {isLead && (EXPORT_TABS as readonly TicketTab[]).includes(tab) ? (
              <a className="ad-button-outline ad-button-sm" href={exportUrl}>
                CSV-Export
              </a>
            ) : null}
            <Link to="/melden" className="ad-button-primary ad-button-sm no-underline">
              + Neues Ticket
            </Link>
          </div>
        }
      >
        Tickets
      </PageTitle>

      <div className="grid gap-6 lg:grid-cols-[212px_minmax(0,1fr)]">
        <aside className="hidden lg:block">{sidebar}</aside>

        <section className="min-w-0 space-y-3">
          {/* Handy/Tablet: Ansichten als Reiter, Team als Auswahl */}
          <div className="space-y-2 lg:hidden">
            <div className="overflow-x-auto">
              <Tabs
                items={VIEWS.map((v) => ({ id: v.id, label: `${v.label}${v.count && counts ? ` (${counts[v.count]})` : ''}` }))}
                active={tab}
                onChange={(id) => go({ tab: id as TicketTab })}
              />
            </div>
            <select className="ad-select ad-select-sm" value={search.teamId ?? ''} onChange={(e) => go({ teamId: e.target.value || undefined })} aria-label="Team">
              <option value="">Alle Teams</option>
              {(teams.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.openCount})
                </option>
              ))}
            </select>
          </div>

          {/* Kopf der Ansicht */}
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b-2 border-steel-800 pb-2">
            <h2 className="flex items-center gap-2 text-[18px]">
              {activeView ? activeView.name : view.label}
              {team ? <span className="font-body text-[15px] font-normal tracking-normal text-steel-600 normal-case">· {team.name}</span> : null}
              <span className="bg-steel-100 px-2 py-0.5 font-mono text-[13px] font-normal text-steel-700">{shown?.total ?? '…'}</span>
            </h2>
            <div className="flex items-center gap-1">
              <IconButton label="Aktualisieren" onClick={() => void list.refetch()} spinning={list.isFetching}>
                <Icon name="refresh" />
              </IconButton>
              <IconButton label="Als Liste" active={!cards} onClick={() => go({ ansicht: undefined }, true)}>
                <Icon name="rows" />
              </IconButton>
              <IconButton label="Als Karten" active={cards} onClick={() => go({ ansicht: 'karten' }, true)}>
                <Icon name="grid" />
              </IconButton>
            </div>
          </div>

          {/* Filterleiste */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-[260px]">
              <span className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-steel-500">
                <Icon name="search" />
              </span>
              <input className="ad-input ad-input-sm pl-8" placeholder="Suche: IH-Nummer, Text, Maschine" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Suche" />
            </div>
            <FilterSelect label="Status" value={search.status ?? ''} onChange={(v) => go({ status: (v || undefined) as TicketStatus | undefined })} options={TICKET_STATUS.map((s) => ({ value: s, label: STATUS_LABEL[s] }))} />
            <FilterSelect label="Priorität" value={search.priority ?? ''} onChange={(v) => go({ priority: (v || undefined) as Priority | undefined })} options={PRIORITY.map((p) => ({ value: p, label: PRIORITY_LABEL[p] }))} />
            {sites.length > 1 && (isLead || (me.data?.user.siteIds.length ?? 0) > 1) ? (
              <FilterSelect
                label="Werk"
                emptyLabel={(me.data?.user.siteIds.length ?? 0) > 0 ? 'Meine Werke' : 'Alle Werke'}
                value={search.siteId ?? ''}
                onChange={(v) => go({ siteId: v || undefined })}
                options={sites.map((s) => ({ value: s.id, label: s.name }))}
              />
            ) : null}
            <FilterSelect
              label="Bearbeiter"
              value={search.assignee ?? ''}
              onChange={(v) => go({ assignee: v || undefined })}
              options={[{ value: 'me', label: 'Mir zugewiesen' }, { value: 'none', label: 'Nicht zugewiesen' }, ...(historyTab ? [] : (workers.data ?? []).map((w) => ({ value: w.id, label: w.displayName })))]}
            />
            <select className="ad-select ad-select-sm w-auto" value={search.sort ?? ''} onChange={(e) => go({ sort: (e.target.value || undefined) as TicketSort | undefined })} aria-label="Sortierung">
              <option value="">Sortierung: Standard</option>
              {(Object.keys(SORT_LABEL) as TicketSort[]).map((s) => (
                <option key={s} value={s}>
                  {SORT_LABEL[s]}
                </option>
              ))}
            </select>
            {filtersActive ? (
              <button type="button" className="min-h-10 cursor-pointer text-[14px] font-medium text-arnold-red underline" onClick={() => void navigate({ search: { tab, teamId: search.teamId } })}>
                Filter zurücksetzen
              </button>
            ) : null}
          </div>

          {shown?.historyLimited ? (
            <Notice>
              Dieses Team hat weniger als {MIN_TEAM_SIZE_FOR_HISTORY} aktive Mitglieder. Behobene und erledigte Tickets erscheinen deshalb nicht unter diesem Team, sonst ließe sich die Arbeit einzelner Personen ablesen. Offene Tickets bleiben sichtbar.
            </Notice>
          ) : null}

          {/* Sammelaktionen */}
          {selectedRows.length ? (
            <div className="flex flex-wrap items-center gap-2 bg-steel-900 px-3 py-2 text-white">
              <span className="text-[15px] font-semibold">{selectedRows.length} ausgewählt</span>
              <span className="mx-1 h-4 w-px bg-steel-700" aria-hidden />
              <button type="button" className="min-h-10 cursor-pointer px-3 text-[15px] font-medium hover:bg-steel-800" onClick={() => void take(selectedRows)}>
                Übernehmen
              </button>
              <button type="button" className="min-h-10 cursor-pointer px-3 text-[15px] font-medium hover:bg-steel-800" onClick={() => setAssign(selectedRows.map(toTarget))}>
                Zuweisen …
              </button>
              <button type="button" className="ml-auto min-h-10 cursor-pointer px-3 text-[15px] text-steel-300 hover:text-white" onClick={() => setSelected(new Set())}>
                Auswahl aufheben
              </button>
            </div>
          ) : null}

          {error ? <ErrorNote error={error} /> : null}

          {list.isPending || staleList ? (
            <Spinner />
          ) : list.isError ? (
            <ErrorNote error={list.error} />
          ) : !items.length ? (
            <EmptyState title="Keine Tickets" hint={tab === 'open' || tab === 'unassigned' ? 'Alles verteilt. Oder die Filter anpassen.' : 'Filter anpassen oder andere Ansicht wählen.'} />
          ) : cards ? (
            <div className="grid gap-2 md:grid-cols-2">
              {items.map((t) => (
                <TicketCard key={t.id} t={t} onTake={() => void take([t])} onAssign={() => setAssign([toTarget(t)])} />
              ))}
            </div>
          ) : (
            <>
              <div className="hidden overflow-x-auto border border-[var(--border-default)] bg-white shadow-[var(--shadow-card)] md:block">
                <table className="w-full border-collapse text-[15px]">
                  <thead>
                    <tr>
                      <th className="ad-th w-10">
                        <input
                          type="checkbox"
                          aria-label="Alle auswählen"
                          className="h-4 w-4 accent-[var(--color-arnold-red)]"
                          checked={items.length > 0 && selected.size === items.length}
                          onChange={(e) => setSelected(e.target.checked ? new Set(items.map((t) => t.id)) : new Set())}
                        />
                      </th>
                      <th className="ad-th">Anfrage</th>
                      <th className="ad-th">Status, wartet auf</th>
                      <th className="ad-th">Zuständig</th>
                      <th className="ad-th hidden 2xl:table-cell">Melder</th>
                      <th className="ad-th">Geändert</th>
                      <th className="ad-th w-10" aria-label="Aktionen" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((t) => (
                      <TicketRow
                        key={t.id}
                        t={t}
                        selected={selected.has(t.id)}
                        onSelect={(on) =>
                          setSelected((prev) => {
                            const next = new Set(prev);
                            if (on) next.add(t.id);
                            else next.delete(t.id);
                            return next;
                          })
                        }
                        onTake={() => void take([t])}
                        onAssign={() => setAssign([toTarget(t)])}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="grid gap-2 md:hidden">
                {items.map((t) => (
                  <TicketCard key={t.id} t={t} onTake={() => void take([t])} onAssign={() => setAssign([toTarget(t)])} />
                ))}
              </div>
            </>
          )}

          {shown && shown.total > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-2 pt-1 text-[13px] text-[var(--text-muted)]">
              <span>
                {(page - 1) * PAGE_SIZE + 1} bis {Math.min(page * PAGE_SIZE, shown.total)} von {shown.total}
              </span>
              {shown.total > PAGE_SIZE ? (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => go({ page: page - 1 }, true)}>
                    Zurück
                  </Button>
                  <Button size="sm" variant="outline" disabled={page * PAGE_SIZE >= shown.total} onClick={() => go({ page: page + 1 }, true)}>
                    Weiter
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Handy: eigene Ansichten unter der Liste */}
          <div className="lg:hidden">
            <MobileSavedViews views={views.data ?? []} onSave={() => setSaveOpen(true)} onApply={(v) => void navigate({ search: v.query as TicketSearch })} />
          </div>
        </section>
      </div>

      {assign ? <AssignDialog tickets={assign} onClose={() => setAssign(null)} onDone={async () => { setSelected(new Set()); await refresh(); }} /> : null}
      {saveOpen ? <SaveViewDialog query={currentQuery} onClose={() => setSaveOpen(false)} /> : null}
    </div>
  );
}

function toTarget(t: TicketListItem): AssignTarget {
  return { id: t.id, version: t.version, display: t.display, siteId: t.siteId, teamId: t.teamId, assigneeId: t.assigneeId, status: t.status };
}

function sortKeys(o: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(o).filter(([k]) => k !== 'ansicht').sort(([a], [b]) => a.localeCompare(b)));
}

function breadcrumb(t: TicketListItem): string {
  return [t.siteCode, t.areaName, t.machineName ? `${t.machineName}${t.machineInventoryNo ? ` (${t.machineInventoryNo})` : ''}` : 'ohne Maschine'].filter(Boolean).join(' › ');
}

function TicketRow({
  t,
  selected,
  onSelect,
  onTake,
  onAssign,
}: {
  t: TicketListItem;
  selected: boolean;
  onSelect: (on: boolean) => void;
  onTake: () => void;
  onAssign: () => void;
}) {
  const navigate = useNavigate();
  const open = () => void navigate({ to: '/tickets/$number', params: { number: t.display } });
  const isOpen = !['resolved', 'closed', 'rejected'].includes(t.status);
  return (
    <tr className={`group cursor-pointer ${selected ? 'bg-arnold-red-tint' : 'hover:bg-steel-50'}`} onClick={open}>
      <td className={`ad-td w-10 ${t.priority === 'p1' && isOpen ? 'border-l-4 border-l-arnold-red' : ''}`} onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" aria-label={`${t.display} auswählen`} className="h-4 w-4 accent-[var(--color-arnold-red)]" checked={selected} onChange={(e) => onSelect(e.target.checked)} />
      </td>
      {/* max-w-0 + w-full: die Anfrage nimmt den Rest der Breite und kürzt statt zu schieben. */}
      <td className="ad-td w-full max-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <PriorityBadge priority={t.priority} short />
          <Link to="/tickets/$number" params={{ number: t.display }} className="min-w-0 truncate font-semibold text-steel-900 no-underline group-hover:text-arnold-red" onClick={(e) => e.stopPropagation()}>
            {t.title}
          </Link>
        </div>
        <div className="mt-1 flex min-w-0 items-center gap-x-2 pl-[42px] text-[14px] text-[var(--text-muted)]">
          <span className="shrink-0 font-mono">{t.display}</span>
          <span className="min-w-0 truncate">
            {breadcrumb(t)}
            {t.reporterName ? <span className="2xl:hidden"> · von {t.reporterName}</span> : null}
          </span>
          {t.needsReview ? <span className="ad-badge shrink-0 bg-steel-200 px-1.5 py-0 text-[12px] text-steel-800">Prüfen</span> : null}
          {t.lockedOut ? <span className="ad-badge shrink-0 bg-steel-800 px-1.5 py-0 text-[12px] text-white">Gesichert</span> : null}
        </div>
      </td>
      <td className="ad-td whitespace-nowrap">
        <StatusBadge status={t.status} />
        <div className="mt-1">
          <WaitingFor status={t.status} hasAssignee={Boolean(t.assigneeId)} />
          {t.waitingUntil ? <span className="ml-1 text-[13px] text-[var(--text-muted)]">bis {fmtDate(t.waitingUntil)}</span> : null}
        </div>
      </td>
      <td className="ad-td max-w-[200px]">
        <Responsible t={t} />
      </td>
      <td className="ad-td hidden max-w-[160px] truncate text-[13px] text-steel-800 2xl:table-cell">{t.reporterName ?? '-'}</td>
      <td className="ad-td text-[14px] whitespace-nowrap text-steel-700" title={fmtDateTime(t.updatedAt)}>
        {fmtAge(t.updatedAt)}
      </td>
      <td className="ad-td w-10" onClick={(e) => e.stopPropagation()}>
        <RowMenu t={t} onTake={onTake} onAssign={onAssign} />
      </td>
    </tr>
  );
}

function Responsible({ t }: { t: TicketListItem }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      {t.assigneeName ? <Avatar name={t.assigneeName} /> : <Avatar name="" kind="none" />}
      <div className="min-w-0 leading-tight">
        <div className={`truncate text-[14px] ${t.assigneeName ? 'text-steel-900' : 'text-[var(--text-muted)] italic'}`}>{t.assigneeName ?? 'Nicht zugewiesen'}</div>
        {/* Abgeschlossene Tickets nennen Kleinstteams nicht (§14.1), „kein Team“ wäre dort falsch. */}
        <div className="truncate text-[13px] text-[var(--text-muted)]">{t.teamName ?? (DONE_STATUSES.includes(t.status) ? '-' : 'kein Team')}</div>
      </div>
    </div>
  );
}

function TicketCard({ t, onTake, onAssign }: { t: TicketListItem; onTake: () => void; onAssign: () => void }) {
  const isOpen = !['resolved', 'closed', 'rejected'].includes(t.status);
  return (
    <div className={`ad-card relative p-4 ${t.priority === 'p1' && isOpen ? 'border-l-4 border-l-arnold-red' : ''}`}>
      <div className="flex flex-wrap items-center gap-2 pr-8">
        <span className="font-mono text-sm text-steel-600">{t.display}</span>
        <PriorityBadge priority={t.priority} short />
        <StatusBadge status={t.status} />
        {t.needsReview ? <span className="ad-badge bg-steel-200 text-steel-800">Prüfen</span> : null}
      </div>
      <div className="absolute top-3 right-3">
        <RowMenu t={t} onTake={onTake} onAssign={onAssign} />
      </div>
      <Link to="/tickets/$number" params={{ number: t.display }} className="mt-1.5 block text-[16px] font-semibold text-steel-900 no-underline">
        {t.title}
      </Link>
      <div className="mt-1 text-[14px] text-[var(--text-muted)]">{breadcrumb(t)}</div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-[var(--border-default)] pt-2.5">
        <Responsible t={t} />
        <div className="text-right text-[14px] text-[var(--text-muted)]">
          <WaitingFor status={t.status} hasAssignee={Boolean(t.assigneeId)} />
          <div title={fmtDateTime(t.updatedAt)}>geändert {fmtAge(t.updatedAt)}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Drei-Punkte-Menü je Zeile (InvGate: Schnellaktionen in der Liste). Fest über der
 * Seite statt in der Zeile: Die Tabelle schneidet ab, was über ihren Rand ragt
 * (seitliches Scrollen), sonst verschwände das Menü in ihr. Unten zu wenig Platz:
 * es klappt nach oben auf. Beim Scrollen schließt es, statt verrutscht zu stehen.
 */
function RowMenu({ t, onTake, onAssign }: { t: TicketListItem; onTake: () => void; onAssign: () => void }) {
  const [pos, setPos] = useState<{ top: number; right: number; up: boolean } | null>(null);
  const open = pos !== null;
  const ref = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setPos(null);
    };
    const dismiss = () => setPos(null);
    document.addEventListener('mousedown', close);
    // capture: auch das Scrollen in der Tabelle selbst schließt das Menü.
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [open]);
  const toggle = (button: HTMLButtonElement) => {
    if (open) {
      setPos(null);
      return;
    }
    const r = button.getBoundingClientRect();
    const up = window.innerHeight - r.bottom < 180;
    setPos({ top: up ? r.top - 4 : r.bottom + 4, right: window.innerWidth - r.right, up });
  };
  // Der Eintrag verschwindet mit dem Menü. Der Knopf bleibt, und ein Dialog kehrt zu ihm zurück.
  const choose = (action: () => void) => {
    trigger.current?.focus();
    setPos(null);
    action();
  };
  const isOpen = !['resolved', 'closed', 'rejected'].includes(t.status);
  return (
    <div ref={ref}>
      <button
        ref={trigger}
        type="button"
        aria-label={`Aktionen für ${t.display}`}
        aria-expanded={open}
        className="cursor-pointer p-1.5 text-steel-600 hover:bg-steel-100 hover:text-steel-900"
        onClick={(e) => toggle(e.currentTarget)}
      >
        <Icon name="more" />
      </button>
      {pos ? (
        <div
          role="menu"
          style={{ position: 'fixed', top: pos.top, right: pos.right, transform: pos.up ? 'translateY(-100%)' : undefined }}
          className="z-[120] w-[200px] border border-[var(--border-default)] bg-white py-1 shadow-[var(--shadow-raised)]"
        >
          {isOpen && !t.isMine ? <MenuItem onClick={() => choose(onTake)}>Übernehmen</MenuItem> : null}
          {isOpen ? <MenuItem onClick={() => choose(onAssign)}>Zuweisen …</MenuItem> : null}
          <Link to="/tickets/$number" params={{ number: t.display }} role="menuitem" className="flex min-h-11 items-center px-4 text-[15px] text-steel-900 no-underline hover:bg-steel-50">
            Öffnen
          </Link>
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" role="menuitem" className="flex min-h-11 w-full cursor-pointer items-center px-4 text-left text-[15px] text-steel-900 hover:bg-steel-50" onClick={onClick}>
      {children}
    </button>
  );
}

function SideItem({
  active,
  onClick,
  icon,
  label,
  sub,
  count,
  extra,
  alert = false,
}: {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  label: string;
  sub?: string | undefined;
  count?: number | undefined;
  extra?: ReactNode;
  alert?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={`flex min-h-10 w-full cursor-pointer items-center gap-2.5 border-l-[3px] px-3 py-2 text-left text-[15px] transition-colors ${
        active ? 'border-arnold-red bg-white font-semibold text-steel-900 shadow-[var(--shadow-card)]' : 'border-transparent text-steel-700 hover:bg-white'
      }`}
    >
      <span className={`shrink-0 ${active ? 'text-arnold-red' : 'text-steel-500'}`}>{icon}</span>
      <span className="min-w-0 flex-1 truncate">
        {label}
        {sub ? <span className="ml-1 text-[12px] font-normal text-[var(--text-muted)]">{sub}</span> : null}
      </span>
      {extra}
      {count !== undefined ? (
        <span className={`min-w-[26px] px-1.5 py-px text-center font-mono text-[12px] ${alert ? 'bg-arnold-red text-white' : 'bg-steel-100 text-steel-700'}`}>{count}</span>
      ) : null}
    </button>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  emptyLabel,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  emptyLabel?: string;
}) {
  return (
    <select
      className={`ad-select ad-select-sm w-auto max-w-[200px] ${value ? 'border-arnold-red bg-arnold-red-tint' : ''}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
    >
      <option value="">{emptyLabel ?? `${label}: alle`}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function IconButton({ label, onClick, active = false, spinning = false, children }: { label: string; onClick: () => void; active?: boolean; spinning?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={`cursor-pointer p-2 ${active ? 'bg-steel-800 text-white' : 'text-steel-600 hover:bg-steel-100 hover:text-steel-900'} ${spinning ? '[&_svg]:animate-spin' : ''}`}
    >
      {children}
    </button>
  );
}

function SaveViewDialog({ query, onClose }: { query: Record<string, string>; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<unknown>(null);
  const save = async () => {
    try {
      await postJson('/api/views', { name, query });
      await qc.invalidateQueries({ queryKey: ['views'] });
      onClose();
    } catch (err) {
      setError(err);
    }
  };
  return (
    <Modal
      open
      title="Ansicht speichern"
      onClose={onClose}
      error={error}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button disabled={name.trim().length < 2} onClick={() => void save()}>
            Speichern
          </Button>
        </>
      }
    >
      <p className="text-sm text-[var(--text-muted)]">Speichert Ansicht, Filter und Sortierung unter einem Namen. Nur für Sie sichtbar, auf allen Geräten.</p>
      <Input label="Name" value={name} onChange={setName} autoFocus placeholder="z. B. P1 und P2 Friedrichsdorf" />
    </Modal>
  );
}

function MobileSavedViews({ views, onSave, onApply }: { views: SavedView[]; onSave: () => void; onApply: (v: SavedView) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-2">
      {views.map((v) => (
        <button key={v.id} type="button" className="ad-tag" onClick={() => onApply(v)}>
          {v.name}
        </button>
      ))}
      <button type="button" className="ad-tag" onClick={onSave}>
        + Ansicht speichern
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Melder: „Meine Meldungen“
// ---------------------------------------------------------------------------

function ReporterTickets() {
  const search = useSearch({ from: '/app/tickets' }) as TicketSearch;
  const navigate = useNavigate({ from: '/tickets' });
  const tab: TicketTab = search.tab === 'open' || search.tab === 'done' ? search.tab : 'all';
  const list = useQuery({
    queryKey: ['tickets', 'list', 'reporter', tab],
    queryFn: () => getJson<TicketListResult>('/api/tickets', { tab, pageSize: 100 }),
    refetchInterval: 30_000,
  });
  return (
    <div className="space-y-5">
      <PageTitle>Meine Meldungen</PageTitle>
      <div className="overflow-x-auto">
        <Tabs
          items={[
            { id: 'all', label: 'Alle' },
            { id: 'open', label: 'Offen' },
            { id: 'done', label: 'Erledigt' },
          ]}
          active={tab}
          onChange={(id) => void navigate({ search: { tab: id as TicketTab } })}
        />
      </div>
      {list.isPending ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorNote error={list.error} />
      ) : !list.data.items.length ? (
        <EmptyState title="Keine Meldungen" hint="Störung melden über den roten Knopf oder den QR-Code an der Maschine." />
      ) : (
        <div className="grid gap-2">
          {list.data.items.map((t) => (
            <Link
              key={t.id}
              to="/tickets/$number"
              params={{ number: t.display }}
              className={`ad-card ad-card-clickable block p-4 no-underline ${t.priority === 'p1' && t.status !== 'closed' ? 'border-l-4 border-l-arnold-red' : ''}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm text-steel-600">{t.display}</span>
                <PriorityBadge priority={t.priority} />
                <StatusBadge status={t.status} />
                <span className="ml-auto text-[13px] text-[var(--text-muted)]">{fmtAge(t.createdAt)}</span>
              </div>
              <div className="mt-1.5 text-[16px] font-semibold text-steel-900">{t.title}</div>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[13px] text-[var(--text-muted)]">
                <span>{breadcrumb(t)}</span>
                {t.waitingUntil ? <span>erwartet {fmtDate(t.waitingUntil)}</span> : null}
                <OriginBadge origin={t.origin} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Symbole (Phosphor, eine Familie für die ganze Anwendung)
// ---------------------------------------------------------------------------

type IconName = 'inbox' | 'userQuestion' | 'user' | 'handshake' | 'clock' | 'eye' | 'check' | 'archive' | 'list' | 'bookmark' | 'plus' | 'x' | 'search' | 'refresh' | 'rows' | 'grid' | 'more';

const ICONS: Record<IconName, PhosphorIcon> = {
  inbox: Tray,
  userQuestion: UserCircleDashed,
  user: UserCircle,
  handshake: Handshake,
  clock: Clock,
  eye: Eye,
  check: CheckCircle,
  archive: Archive,
  list: ListBullets,
  bookmark: BookmarkSimple,
  plus: Plus,
  x: X,
  search: MagnifyingGlass,
  refresh: ArrowClockwise,
  rows: Rows,
  grid: SquaresFour,
  more: DotsThreeVertical,
};

function Icon({ name, size = 20 }: { name: IconName; size?: number }) {
  const Cmp = ICONS[name];
  return <Cmp size={size} className="block" aria-hidden />;
}
